import { useState, useEffect } from 'react';
import { CheckCircle, RefreshCw } from 'lucide-react';
import Modal from '../common/Modal';
import { useToast } from '../common/ToastContainer';
import { routingPolicyService } from '../../services';
import type { RoutingPolicy } from '../../services';

interface ApplyTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  edgeId: string;
  tenantId?: string;
  onApplied: () => void;
}

const ApplyTemplateModal = ({ isOpen, onClose, edgeId, tenantId, onApplied }: ApplyTemplateModalProps) => {
  const toast = useToast();
  const [templates, setTemplates] = useState<RoutingPolicy[]>([]);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
    }
  }, [isOpen]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await routingPolicyService.list({ pageSize: 100, tenantId });
      // Only show templates (policies without edge_id)
      const templatePolicies = response.items.filter(p => !p.edge_id);
      setTemplates(templatePolicies);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      toast.error('Load Failed', 'Failed to load policy templates');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async (templateId: string) => {
    try {
      setApplying(templateId);
      await routingPolicyService.applyTemplate(templateId, edgeId);
      toast.success('Template Applied', 'Policy template applied to this edge');
      onApplied();
    } catch (error: any) {
      console.error('Failed to apply template:', error);
      const msg = error?.response?.data?.error?.message || error?.response?.data?.error || 'Failed to apply policy template';
      toast.error('Apply Failed', typeof msg === 'string' ? msg : 'Failed to apply policy template');
    } finally {
      setApplying(null);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Apply Policy Template"
      size="lg"
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Select a policy template to apply to this edge. A copy of the template will be created with edge-specific settings that you can customize.
        </p>

        {loading ? (
          <div className="flex items-center justify-center h-32">
            <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : templates.length === 0 ? (
          <div className="text-sm text-gray-500 bg-gray-50 rounded-lg p-6 text-center">
            <p>No policy templates available.</p>
            <p className="mt-1 text-xs">Create templates in the Policy Templates page first.</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {templates.map((template) => (
              <div
                key={template.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-primary-300 transition-colors"
              >
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{template.name}</div>
                  {template.description && (
                    <div className="text-xs text-gray-500 mt-0.5">{template.description}</div>
                  )}
                  <div className="flex items-center space-x-3 mt-1">
                    <span className="text-xs text-gray-500">
                      Priority: {template.priority}
                    </span>
                    <span className="text-xs text-gray-500">
                      {template.rules?.length || 0} rule{(template.rules?.length || 0) !== 1 ? 's' : ''}
                    </span>
                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                      template.enabled === 1 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {template.enabled === 1 ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => handleApply(template.id)}
                  disabled={applying !== null}
                  className="btn-primary inline-flex items-center space-x-1.5 text-sm py-1.5 px-3 ml-4 disabled:opacity-50"
                >
                  {applying === template.id ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4" />
                  )}
                  <span>{applying === template.id ? 'Applying...' : 'Apply'}</span>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ApplyTemplateModal;
